import { useState } from "react";
import { FileSignature, CheckCircle2, Loader2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

import RichContent from "@/components/RichContent";
function AgreementCard({ a, onSign, defaultName }) {
  const [sig, setSig] = useState(defaultName || "");
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState(false);
  const submit = async () => { setBusy(true); try { await onSign(a.id, sig, agree); } finally { setBusy(false); } };
  return (
    <div className="rounded-2xl border border-[#E5E0D8] bg-white p-4" data-testid={`agreement-card-${a.id}`}>
      <p className="text-[11px] uppercase tracking-wide text-[#8FA89B]">{a.type} · version {a.version}</p>
      <p className="font-serif text-lg text-[#1A242B]">{a.title}</p>
      {a.pdf_path && (
        <a href={`/api/files/${a.pdf_path}`} target="_blank" rel="noreferrer" data-testid={`agreement-pdf-${a.id}`} className="mt-2 inline-flex items-center gap-1.5 text-sm text-[#144F75] underline">
          <FileSignature size={14} /> Read the full document (PDF{a.pdf_filename ? ` · ${a.pdf_filename}` : ""})
        </a>
      )}
      {a.content && <div className="mt-2 max-h-40 overflow-y-auto rounded-xl bg-[#F9F7F3] p-3 text-xs text-[#4A5D6B] leading-relaxed" tabIndex={0}><RichContent text={a.content} testid={`agreement-content-${a.id}`} /></div>}
      <label className="text-xs text-[#4A5D6B] mt-3 mb-1 block" htmlFor={`sig-${a.id}`}>Type your full name to sign</label>
      <input id={`sig-${a.id}`} value={sig} onChange={(e) => setSig(e.target.value)} data-testid={`agreement-signature-${a.id}`} className="eyw-input w-full font-serif" placeholder="Full name" />
      <label className="flex items-start gap-2 text-xs text-[#4A5D6B] mt-2">
        <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} data-testid={`agreement-agree-${a.id}`} className="accent-[#144F75] mt-0.5" />
        I agree — I have read this {a.title} and my typed name is my electronic signature.
      </label>
      <button type="button" onClick={submit} disabled={busy || !agree || !sig.trim()} data-testid={`agreement-sign-${a.id}`} className="eyw-btn w-full justify-center mt-3 disabled:opacity-50">
        {busy && <Loader2 size={16} className="animate-spin" />} Sign
      </button>
    </div>
  );
}

// Review & sign each pending agreement for a booking. Signed-in: by booking id; guest: by private token.
export default function AgreementsStep({ bookingId, token, initialPending, defaultName, onDone, onLater }) {
  const [pending, setPending] = useState(initialPending || []);
  const sign = async (aid, signature, consent) => {
    const url = token ? `/public/booking-agreements/${token}/${aid}/sign` : `/bookings/${bookingId}/agreements/${aid}/sign`;
    try {
      const { data } = await api.post(url, { signature, consent });
      setPending(data.pending);
      toast.success("Signed.");
      if (!data.pending.length) onDone && onDone();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Could not sign."); }
  };
  if (!pending.length) {
    return (
      <div className="text-center py-6" data-testid="agreements-all-signed">
        <CheckCircle2 size={40} className="text-[#5C8A72] mx-auto mb-2" />
        <p className="text-[#1A242B]">All agreements are signed.</p>
      </div>
    );
  }
  return (
    <div data-testid="agreements-step">
      <p className="eyw-label mb-1 flex items-center gap-2"><FileSignature size={14} /> Before your session</p>
      <h3 className="font-serif text-2xl text-[#1A242B]">Review & sign {pending.length === 1 ? "your agreement" : `${pending.length} agreements`}</h3>
      <p className="text-sm text-[#4A5D6B] mt-1">Your booking is held and will be fully confirmed once these are signed. Each is saved with the date and version you sign.</p>
      <div className="space-y-3 mt-4 max-h-[55vh] overflow-y-auto pr-0.5">
        {pending.map((a) => <AgreementCard key={a.id} a={a} onSign={sign} defaultName={defaultName} />)}
      </div>
      {onLater && (
        <button type="button" onClick={onLater} data-testid="agreements-sign-later" className="w-full mt-3 rounded-full border border-[#E5E0D8] bg-white px-4 py-2.5 text-sm text-[#4A5D6B] hover:bg-[#F9F7F3]">
          Sign later — keep my booking on hold
        </button>
      )}
    </div>
  );
}
